import { CancelToken } from 'axios';
import { DataAnalysis, DataAnalysisTwoPointFive, Dish, Order } from '../../models';
import type { PaymentQueueItem } from '../../models/payment-queue.entity';
import { CurrentData } from '../../models/abstract/current-data.entity';
import {
    CurrentBusinessDataResponse,
    CurrentBusinessDataType,
} from '../../models/abstract/current-business-data.entity';
import { HistoryPeriod } from '../../models/abstract/history-period.entity';
import { DataAnalysisCartogram } from '../../models/data-analysis-cartogram.entity';
import { BusinessSummaryData, CurrentOrderData, HistoryCancelled, HistoryOrderData } from '../../models/fixed/data-analysis';

import { getApiLanguageCode, type ApiLanguageCode } from '../../constants/api-language';
import http, { HttpSuccessOrErrorType } from '../http';
import { getCurrentBusinessDataTimeRange } from '../../constants/tool-order';

/**
 * 营业分析订单详情接口返回的订单头摘要（`order_detail_with_head` 的 `order_head`）。
 */
export interface DataAnalysisOrderHeadSummary {
    /** 订单头 ID */
    order_head_id: number;
    /** 账单号 */
    check_number?: number;
    /** 分单 ID */
    check_id?: number;
    /** 就餐方式：0 堂吃等 */
    eat_type?: number;
    /** 桌台名称 */
    table_name?: string | null;
    /** 桌台 ID */
    table_id?: number | null;
    /** 客人数 */
    customer_num?: number | null;
    /** 应付金额，字符串金额 */
    should_amount?: string | null;
    /** 实付金额，字符串金额 */
    paid_amount?: string | null;
    /** 总金额，字符串金额 */
    total_amount?: string | null;
    /** 开台时间 */
    order_start_time?: string | Date | null;
    /** KDS 时间 */
    kds_time?: string | Date | null;
    /** 结单时间 */
    order_end_time?: string | Date | null;
    /** 开台员工 ID */
    open_employee_id?: number | null;
    /** 开台员工名称 */
    open_employee_name?: string | null;
    /** 折扣总额 */
    discount_total?: string | number | null;
    /** 退菜总额 */
    refund_dish_total?: string | number | null;
    /** 改价总额 */
    reprice_total?: string | number | null;
    /** 菜品数量 */
    dish_count?: number | null;
    /** 人均菜品数量 */
    avg_dish_per_person?: number | null;
    /** 付款队列（部分接口嵌在 order_head 内） */
    queue?: PaymentQueueItem[];
}

/**
 * 营业分析订单详情（含订单头 + 菜品明细）接口响应体。
 */
export interface DataAnalysisOrderDetailWithHeadResponse {
    /** 订单头摘要 */
    order_head: DataAnalysisOrderHeadSummary;
    /** 菜品明细列表 */
    details: Dish[];
    /** 付款队列（部分接口在响应根级返回） */
    queue?: PaymentQueueItem[];
}

/**
 * 将接口金额字段（字符串或数字）转为 number，无效时返回 null。
 * @param raw 原始金额
 */
function parseDataAnalysisOrderAmount(raw: string | number | null | undefined): number | null {
    if (raw === null || raw === undefined || raw === '') {
        return null;
    }
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
}

/**
 * 将 `order_detail_with_head` 的 `order_head` 转为页面用的 Order 实体。
 * @param head 接口订单头
 */
export function mapDataAnalysisOrderHeadToOrder(head: DataAnalysisOrderHeadSummary): Order {
    const order = new Order();
    order.order_head_id = head.order_head_id;
    order.check_number = head.check_number;
    order.check_id = head.check_id != null ? String(head.check_id) : undefined;
    order.eat_type = head.eat_type;
    order.table_name = head.table_name ?? null;
    order.table_id = head.table_id ?? null;
    order.customer_num = head.customer_num ?? undefined;
    order.should_amount = parseDataAnalysisOrderAmount(head.should_amount);
    order.total_amount = parseDataAnalysisOrderAmount(head.total_amount);
    order.paid_amount = parseDataAnalysisOrderAmount(head.paid_amount);
    if (head.order_start_time) {
        order.order_start_time = new Date(head.order_start_time);
    }
    if (head.kds_time) {
        order.kds_time = new Date(head.kds_time);
    }
    order.order_end_time = head.order_end_time != null ? String(head.order_end_time) : undefined;
    order.open_employee_name = head.open_employee_name ?? undefined;
    order.discount_total = parseDataAnalysisOrderAmount(head.discount_total);
    order.refund_dish_total = parseDataAnalysisOrderAmount(head.refund_dish_total);
    order.reprice_total = parseDataAnalysisOrderAmount(head.reprice_total);
    order.dish_count = head.dish_count ?? undefined;
    order.avg_dish_per_person = head.avg_dish_per_person ?? undefined;
    if (Array.isArray(head.queue) && head.queue.length > 0) {
        order.queue = normalizePaymentQueueItems(head.queue);
    }
    return order;
}

/**
 * 展开混合支付子项为多行付款队列（与 POS history-orders-page 一致）。
 * @param rawQueue 接口原始 queue 数组
 */
export function normalizePaymentQueueItems(rawQueue: PaymentQueueItem[]): PaymentQueueItem[] {
    const result: PaymentQueueItem[] = [];
    for (const queue of rawQueue) {
        const mixed = queue.mixed_payment;
        if (Array.isArray(mixed) && mixed.length > 0) {
            for (const type of mixed) {
                const amount = Number(type.amount ?? 0);
                const tip = Number(type.tipAmount ?? 0);
                result.push({
                    ...queue,
                    payment_type_name: type.name ?? queue.payment_type_name,
                    discounted_total_price: amount - tip,
                    tip_amount: type.tipAmount ?? 0,
                    service_amount: type.serviceAmount ?? 0,
                });
            }
        } else {
            result.push(queue);
        }
    }
    return result;
}

/**
 * 从订单详情响应中取出付款队列（兼容根级 queue 与 order_head.queue）。
 * @param payload 接口响应体
 */
function extractPaymentQueueFromOrderDetailPayload(
    payload: DataAnalysisOrderDetailWithHeadResponse
): PaymentQueueItem[] {
    if (Array.isArray(payload.queue) && payload.queue.length > 0) {
        return normalizePaymentQueueItems(payload.queue);
    }
    const headQueue = payload.order_head?.queue;
    if (Array.isArray(headQueue) && headQueue.length > 0) {
        return normalizePaymentQueueItems(headQueue);
    }
    return [];
}

/**
 * 订单详情 Query 的 check_id：列表/接口可能为 number，空值返回 undefined（不传参）。
 * @param check_id 分单号
 */
export function resolveOrderDetailCheckIdQuery(
    check_id?: string | number | null
): string | undefined {
    if (check_id == null || check_id === '') {
        return undefined;
    }
    const trimmed = String(check_id).trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

/** `get_data_analysis_order_details` 解析结果：订单头 + 菜品明细 */
export type ParsedDataAnalysisOrderDetail = {
    /** 订单头，供详情摘要区展示 */
    order: Order | null;
    /** 菜品明细 */
    details: Dish[] | null;
};

/**
 * 判断订单详情字段是否有可用值（与列表行合并时，仅在有值时采用接口数据）。
 * @param value 单字段原始值
 */
function hasOrderDetailFieldValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    if (typeof value === 'string') {
        return value.trim().length > 0;
    }
    if (typeof value === 'number') {
        return !Number.isNaN(value);
    }
    if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    return true;
}

/**
 * 复制列表行订单作为详情摘要占位（打开弹窗即展示表格字段，避免等接口时空白）。
 * @param row 列表行订单
 */
export function cloneOrderDetailTableRowFallback(row: Order): Order {
    return { ...row };
}

/**
 * 将接口 `order_head` 与列表行合并：接口有值的字段覆盖列表，缺省保留列表行。
 * @param tableRow 打开详情时的列表行（或由其派生的 stub）
 * @param apiOrder 接口解析后的订单头，可为 null
 */
export function mergeOrderDetailSummaryWithTableRow(tableRow: Order, apiOrder: Order | null): Order {
    const merged = cloneOrderDetailTableRowFallback(tableRow);
    if (!apiOrder) {
        return merged;
    }
    const keys = Object.keys(apiOrder) as (keyof Order)[];
    for (const key of keys) {
        const apiValue = apiOrder[key];
        if (hasOrderDetailFieldValue(apiValue)) {
            (merged as Record<string, unknown>)[key as string] = apiValue;
        }
    }
    return merged;
}

/**
 * 历史订单详情：接口实付缺省时用应付兜底（在合并进列表行之前处理）。
 * @param apiOrder 接口订单头
 */
export function applyHistoryOrderDetailAmountFallback(apiOrder: Order | null): Order | null {
    if (!apiOrder) {
        return null;
    }
    if (apiOrder.actual_amount == null && apiOrder.total_amount != null) {
        apiOrder.actual_amount = apiOrder.total_amount;
    }
    return apiOrder;
}

/**
 * 解析 `get_data_analysis_order_details` 完整响应（订单头 + 菜品）。
 * @param res 接口原始响应（可能带 error）
 */
export function parseDataAnalysisOrderDetailWithHead(
    res: HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse> | Dish[]
): ParsedDataAnalysisOrderDetail {
    if (res && typeof res === 'object' && 'error' in res && (res as { error?: boolean }).error) {
        return { order: null, details: null };
    }
    if (Array.isArray(res)) {
        return { order: null, details: res };
    }
    const payload = res as DataAnalysisOrderDetailWithHeadResponse;
    const order = payload?.order_head ? mapDataAnalysisOrderHeadToOrder(payload.order_head) : null;
    const details = Array.isArray(payload?.details) ? payload.details : null;
    if (order) {
        const queue = extractPaymentQueueFromOrderDetailPayload(payload);
        if (queue.length > 0) {
            order.queue = queue;
        }
    }
    return { order, details };
}

/**
 * 将 `get_data_analysis_order_details` 响应解析为菜品列表。
 * @param res 接口原始响应（可能带 error）
 * @returns 菜品列表；错误或结构无效时返回 null
 */
export function parseDataAnalysisOrderDetailsResponse(
    res: HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse> | Dish[]
): Dish[] | null {
    return parseDataAnalysisOrderDetailWithHead(res).details;
}

/**
 * 餐厅菜单销售分析模式。
 */
export enum RestaurantMenuSalesAnalysisMode {
    /** 今日 */
    Today = 'today',
    /** 本月 */
    Month = 'month',
    /** 本周 */
    Week = 'week',
    /** 自定义时间 */
    Custom = 'custom'
}

/**
 * 餐厅菜单销售分析维度。
 */
export enum RestaurantMenuSalesAnalysisType {
    /** 菜单销售分析 */
    Menu = 'menu',
    /** 菜单类别销售分析 */
    Category = 'category',
    /** 菜单菜品销售分析 */
    Item = 'item'
}

/**
 * 菜品小类选项。
 */
export interface DishCategoryOption {
    /** 二级菜单唯一 ID */
    dmi_slu_id?: number;
    /** 二级菜单名称 */
    dmi_slu_name?: string;
}

/**
 * 一级菜单筛选项。
 */
export interface DishCategoryItem {
    /** 一级菜单唯一 ID */
    main_group_id?: number;
    /** 一级菜单名称 */
    main_group_name?: string;
    /** 当前一级菜单下的菜品小类列表 */
    menu_item_slu?: DishCategoryOption[];
}

/**
 * 菜单销售分析趋势点。
 */
export interface RestaurantMenuSalesTrendPoint {
    /** 横轴时间点文本 */
    x: string;
    /** 对应指标值 */
    y: number;
}

/**
 * 额外费用分析趋势点。
 */
export interface ServicePeriodAnalysisTrendPoint {
    /** 横轴日期（YYYY-MM-DD）。 */
    x: string;
    /** 当日销售数量。 */
    quantity: number;
    /** 当日销售金额。 */
    amount: number;
}

/**
 * 额外费用分析列表项。
 */
export interface ServicePeriodAnalysisListItem {
    /** 额外费用名称。 */
    menu_item_name: string;
    /** 销售数量汇总。 */
    sum_quantity: number;
    /** 销售金额汇总。 */
    sum_price: number;
    /** 数量占比。 */
    count_percentage: number;
    /** 金额占比。 */
    price_percentage: number;
    /** 按日期展开的趋势数据。 */
    daily_curve: ServicePeriodAnalysisTrendPoint[];
}

/**
 * 额外费用分析响应。
 */
export interface ServicePeriodAnalysisResponse {
    /** 查询模式。 */
    mode: RestaurantMenuSalesAnalysisMode | string;
    /** 本次查询时间范围。 */
    time_range: {
        /** 起始时间。 */
        start: string;
        /** 结束时间。 */
        end: string;
    };
    /** 额外费用列表。 */
    list: ServicePeriodAnalysisListItem[];
}

/**
 * 额外费用分析入参。
 */
export interface ServicePeriodAnalysisParams {
    /** 餐厅 string_id。 */
    string_id: string;
    /** 查询模式：today / month / week / custom。 */
    mode: RestaurantMenuSalesAnalysisMode;
    /** 自定义模式开始时间。 */
    start_time?: string;
    /** 自定义模式结束时间。 */
    end_time?: string;
}

/**
 * 餐厅菜单销售分析列表项。
 */
export interface RestaurantMenuSalesAnalysisListItem {
    /** 当前行唯一 ID */
    id: number;
    /** 菜单名称 */
    menu_name?: string;
    /** 菜单类别名称 */
    category_name?: string;
    /** 菜品名称 1 */
    item_name1?: string;
    /** 菜品名称 2 */
    item_name2?: string;
    /** 销售数量 */
    total_quantity: number;
    /** 销售总额 */
    total_amount: number;
    /** 退餐率，百分比数值 */
    return_rate?: number;
    /** 销售占比，百分比数值 */
    sales_ratio?: number;
    /** 单价 */
    unit_price?: number;
    /** 销售数量趋势线 */
    quantity_curve?: RestaurantMenuSalesTrendPoint[];
    /** 销售金额趋势线 */
    amount_curve?: RestaurantMenuSalesTrendPoint[];
}

/**
 * 餐厅菜单销售分析响应。
 */
export interface RestaurantMenuSalesAnalysisResponse {
    /** 餐厅 string_id */
    string_id: string;
    /** 查询模式 */
    mode: RestaurantMenuSalesAnalysisMode | string;
    /** 当前分析维度 */
    type: RestaurantMenuSalesAnalysisType | string;
    /** 列表数据 */
    list: RestaurantMenuSalesAnalysisListItem[];
}

/**
 * 餐厅菜单销售分析入参。
 */
export interface RestaurantMenuSalesAnalysisParams {
    /** 分析维度：菜单 / 类别 / 菜品 */
    type: RestaurantMenuSalesAnalysisType;
    /** 查询模式：today / month / week / custom */
    mode: RestaurantMenuSalesAnalysisMode;
    /** 自定义模式开始时间，格式 YYYY-MM-DD HH:mm:ss */
    start_time?: string;
    /** 自定义模式结束时间，格式 YYYY-MM-DD HH:mm:ss */
    end_time?: string;
    /** 一级菜单 ID；分类/菜品分析时按需传入 */
    menu_id?: string;
    /** 二级菜单 ID；菜品分析时按需传入 */
    category_id?: string;
}

/**
 * 菜品分析智能总结类型。
 */
export type DataAnalysisAiSummaryType = 'service_extra_fee' | 'menu_sales' | 'dish_efficiency';

/**
 * 菜品分析智能总结请求入参。
 */
export interface DataAnalysisAiSummaryRequest {
    /** 餐厅 string_id。 */
    string_id: string;
    /** 智能分析类型。 */
    type: DataAnalysisAiSummaryType;
    /** 餐厅名称（可选）。 */
    restaurant_name?: string;
    /** 菜单销售维度（仅 menu_sales 需要）。 */
    menu_sales_analysis_type?: RestaurantMenuSalesAnalysisType;
    /**
     * 分析源数据：`mode`、`time_range` 与列表接口一致；另传 `list` 为当前表格行。
     * 菜单销售须从 list 项中去掉 `quantity_curve`；额外费用须去掉 `daily_curve`（见页面侧序列化工具）。
     */
    payload: Record<string, unknown> | unknown[];
    /**
     * AI 输出语言（CN / EN / IT / SP）；未传时由 API 层取当前 i18n 语言
     */
    language?: ApiLanguageCode;
}

export type { ApiLanguageCode };

/**
 * 菜品分析智能总结结果。
 */
export interface DataAnalysisAiSummaryResponse {
    /** 智能分析类型。 */
    type: string;
    /** 结构化分析结果。 */
    analysis: {
        /** 标题。 */
        headline: string;
        /** 要点列表。 */
        points: string[];
        /** 总结文本。 */
        summary: string;
    };
    /** 兜底原始文本。 */
    raw_text?: string;
}

const api = {
    /**
     * 查询指定餐厅的所有 rule 资源
     * @param string_id 参数
     */
    async get_data_analysis_string_id_refresh(params: { string_id: string, start?: string, end?: string, data_type: string }): Promise<HttpSuccessOrErrorType<Array<DataAnalysis>>> {
        const { string_id, ...rest } = params;
        return await http.get(`/data_analysis/${string_id}/refresh`, {
            params: {
                ...rest
            }
        })
    },

    /**
     * 员工行为分析 所有员工的退菜信息（退菜的数量、退菜的总金额）
     * @param string_id 参数
     */
    async get_data_analysis_history_cancelled_period(params:
        {
            string_id: string;
            start: string;
            end: string;
        }, config?: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<HistoryCancelled[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/history_cancelled/period`, {
            params: {
                ...rest
            },
            ...config,
        })
    },

    /**
     * 获取折线统计图数据 (日期  总额度  堂食总额  外卖总额)
     * @param string_id 参数
     */
    async get_data_analysis_history_cartogram(params: { string_id: string, start: string, end: string }, config?: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<DataAnalysisCartogram>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/history/cartogram`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 获取餐厅即时运营信息（旧接口，保留兼容）
     * @param string_id 参数
     */
    async get_data_analysis_current_data(params: { string_id: string, start?: string, end?: string }, config?: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<CurrentData>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/current_data`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 当前营业数据（新接口：当前状态、风控、已结/未结订单）
     * @param string_id 餐厅码
     * @param type 时间段：'0' 全天，'1' 午餐，'2' 晚餐
     */
    async get_current_business_data(
        params: { string_id: string; type?: CurrentBusinessDataType },
        config?: { cancelToken: CancelToken }
    ): Promise<HttpSuccessOrErrorType<CurrentBusinessDataResponse>> {
        const { string_id, type = '0' } = params;
        const { start, end } = getCurrentBusinessDataTimeRange(type);
        return await http.get(`/restaurant/${string_id}/current-business-data`, {
            params: { type, start, end },
            ...config,
        });
    },

    /**
     * 餐厅菜品分析：销量前十名菜品（总数量、总金额、在所有菜品的整体占比）
     * @param string_id 参数
     */
    async get_data_analysis_dish_period(string_id: string, params: {
        // 开始时间
        start: string,
        // 结束时间
        end: string,
        main_group_id?: string | number,
        menu_start: string | undefined,
        menu_end: string | undefined,
        // 日期模式/时间段模式
        isDateMode: boolean,
        // 数量总计
        sum_quantity?: number;
        // 价格总计
        sum_price?: number;
        // 已销售/未销售
        not_sold: boolean;
        // 已销售必传 排名排序
        sort?: string;
        top?: string,
    }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<Array<DataAnalysis>>> {

        const { start, end } = params;
        // 由于日期跨度大时，请求时间过长导致超时，故分三步请求数据
        const [sumQuantity, sumPrice] = await Promise.all([
            http.get(`/restaurant/${string_id}/data_analysis/dish/period/sum_quantity`, {
                params: {
                    start,
                    end
                },
                ...config
            }),
            http.get(`/restaurant/${string_id}/data_analysis/dish/period/sum_price`, {
                params: {
                    start,
                    end
                },
                ...config
            })
        ])
        const sumQuantityRes = sumQuantity as {
            total_sum_quantity?: number;
            error?: boolean;
        }
        const sumPriceRes = sumPrice as {
            total_sum_price?: number;
            error?: boolean;
        }
        if (!sumQuantityRes?.error) {
            params.sum_quantity = sumQuantityRes['total_sum_quantity'] || 0
        }
        if (!sumPriceRes?.error) {
            params.sum_price = sumPriceRes['total_sum_price'] || 0
        }

        // 菜品下单开始时间menu_start 菜品下单结束时间menu_end
        return await http.get(`/restaurant/${string_id}/data_analysis/dish/period`, {
            params,
            ...config
        })
    },

    /**
     * 餐馆额外费用分析（时间段）：销量前十名额外费用（总数量、总金额、在所有额外费用的整体占比）
     * @param string_id 参数
     */
    async get_data_analysis_service_period(params: { string_id: string, start: string, end: string }, config?: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<Array<DataAnalysis>>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/service/period`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 餐馆额外费用分析（模式版）：返回列表与按日销量/金额曲线。
     * @param params - 查询参数
     * @param config - axios 额外配置
     */
    async get_data_analysis_service_period_analysis(
        params: ServicePeriodAnalysisParams,
        config?: { cancelToken: CancelToken }
    ): Promise<HttpSuccessOrErrorType<ServicePeriodAnalysisResponse>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/service/period_analysis`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 菜品出菜效率分析（时间段）:菜品名称1，岗位名称（打印组名称），数量，下单时间(order_time)，菜品状态最后更新时间（end_time），两个时间的差值
     * @param string_id 参数
     */
    async get_data_analysis_service_dish_efficiency_period(params: {
        string_id: string,
        start: string,
        end: string,
        main_group_id?: string,
        dmi_slu_id?: string,
    }, config?: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<Array<DataAnalysis>>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/dish/efficiency/period`, {
            params: {
                ...rest
            },
            ...config
        })
    },


    /**
     * 餐厅营业数据 历史订单的总人数、历史订单成人数、历史订单小孩数、历史订单桌台数量、历史订单菜品数量、历史订单实收金额（折扣后的）、历史订单退菜金额、折扣总额、按照支付方式总结总额
     * @param string_id 参数
     */
    async get_data_analysis_history_period(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<HistoryPeriod>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/history/period`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 餐厅营业数据 历史订单的总人数、历史订单成人数、历史订单小孩数、历史订单桌台数量、历史订单菜品数量、历史订单实收金额（折扣后的）、历史订单退菜金额、折扣总额、按照支付方式总结总额
     * @param string_id 参数
     */
    async get_data_analysis_history_cancelled_dish_period(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<DataAnalysis[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/history_cancelled/dish/period`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    async get_critical_operations(
        params: { string_id: string, start: string, end: string },
        config?: { cancelToken: CancelToken }
    ): Promise<HttpSuccessOrErrorType<DataAnalysis[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/critical_operations/period`, {
            params: {
                ...rest
            },
            ...config,
        })
    },

    // 2.5版本获取餐厅分析数据
    async get_data_analysis_res_string_id(params: { string_id: string }): Promise<HttpSuccessOrErrorType<DataAnalysisTwoPointFive>> {
        const { string_id } = params;
        return await http.get(`/data-analysis/res_string_id/${string_id}`, {
        })
    },

    // 获取即时数据
    async get_data_analysis_res_immediate_order(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<CurrentOrderData>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/order_data`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    // 每日营业汇总
    async get_data_analysis_res_business_summary_every_day(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<BusinessSummaryData[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/history/every_day`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    // 获取历史订单数据（堂食 / 外卖筛选参数独立：dine_*、takeout_*）
    async get_data_analysis_res_history_order(
        params: {
            string_id: string;
            start: string;
            end: string;
            dine_table_name?: string[];
            takeout_table_name?: string[];
            dine_order_no?: string;
            takeout_order_no?: string;
        },
        config: { cancelToken?: CancelToken } = {}
    ): Promise<HttpSuccessOrErrorType<HistoryOrderData>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/history_order/type`, {
            params: {
                ...rest
            },
            ...config
        })
    },

    /**
     * 云端历史订单详情（订单头 + 菜品明细），与 `order_detail_with_head` 响应结构一致。
     * @param params string_id、id（通常为 server_id；敏感操作无 server_id 时可为 order_head_id 并带 start/end）
     */
    async get_data_analysis_res_history_order_details(params: {
        string_id: string;
        id: string;
        /** 敏感操作按 order_head_id 定位时：列表筛选开始时间 */
        start?: string;
        /** 敏感操作按 order_head_id 定位时：列表筛选结束时间 */
        end?: string;
        /** 菜品排序，默认后端 DESC */
        sort?: 'ASC' | 'DESC';
    }): Promise<HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse>> {
        const { string_id, ...res } = params;
        return await http.get(`/restaurant/${string_id}/history_order/head`, {
            params: {
                ...res
            }
        })
    },

    /**
     * 获取订单详情（订单头 + 菜品明细）。
     * @param params string_id、order_head_id、可选 check_id、sort
     */
    async get_data_analysis_order_details(params: { string_id: string, order_head_id: string, check_id?: string | number, sort: string }): Promise<HttpSuccessOrErrorType<DataAnalysisOrderDetailWithHeadResponse>> {
        const { string_id, check_id, ...rest } = params;
        const queryParams: { order_head_id: string; sort: string; check_id?: string } = { ...rest };
        const trimmedCheckId = resolveOrderDetailCheckIdQuery(check_id);
        if (trimmedCheckId) {
            queryParams.check_id = trimmedCheckId;
        }
        return await http.get(`/restaurant/${string_id}/data_analysis/order_detail_with_head`, {
            params: queryParams,
        })
    },

    // 获取菜品大类
    async get_data_analysis_res_dish_Category(string_id: string): Promise<HttpSuccessOrErrorType<DishCategoryItem[]>> {
        return await http.get(`/restaurant/${string_id}/data_analysis/main_group_name`, {
        })
    },

    /**
     * 餐厅菜单销售分析。
     * @param string_id 餐厅 string_id
     * @param params 查询参数
     */
    async get_menu_sales_analysis(
        string_id: string,
        params: RestaurantMenuSalesAnalysisParams,
        config?: { cancelToken: CancelToken }
    ): Promise<HttpSuccessOrErrorType<RestaurantMenuSalesAnalysisResponse>> {
        return await http.get(`/restaurant/${string_id}/menu-sales-analysis`, {
            params,
            ...config,
        })
    },

    /**
     * 菜品分析 AI 智能总结。
     * @param params - AI 总结入参
     */
    async post_data_analysis_ai_summary(
        params: DataAnalysisAiSummaryRequest
    ): Promise<HttpSuccessOrErrorType<DataAnalysisAiSummaryResponse>> {
        const { string_id, language, ...rest } = params;
        return await http.post(`/restaurant/${string_id}/data_analysis/ai_summary`, {
            ...rest,
            language: language ?? getApiLanguageCode(),
        });
    },

    // 获取菜品岗位组
    async get_data_analysis_print_class_name(string_id: string): Promise<HttpSuccessOrErrorType<Array<{ print_class_id: number; print_class_name: string }>>> {
        return await http.get(`/restaurant/${string_id}/data_analysis/print_class_name`, {
        })
    },

    /**
     * 即时退菜详情
     * @param params 
     * @returns 
     */

    async get_data_analysis_cancelled_dish(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<DataAnalysis[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/cancelled/dish`, {
            params: {
                ...rest
            },
            ...config
        })
    },


    /**
     * 即时敏感操作分析
     * @param stringId 
     * @returns 
     */
    async get_data_analysis_critical_operations(params: { string_id: string, start: string, end: string }, config: { cancelToken: CancelToken }): Promise<HttpSuccessOrErrorType<DataAnalysis[]>> {
        const { string_id, ...rest } = params;
        return await http.get(`/restaurant/${string_id}/data_analysis/critical_operations`, {
            params: {
                ...rest
            },
            ...config
        })
    }

}
export default api